import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { Listbox, ListboxModule } from 'primeng/listbox';
import { Popover, PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppStateService } from '../services/app-state.service';
import { EVENT_APPLY_COLUMN_FILTER } from '../services/logging-constants';
import { LoggingService } from '../services/logging.service';
import { ColumnFilterService, ITableColumnFilter } from './column-filter.service';

@Component({
  selector: 'app-column-filter',
  templateUrl: './column-filter.component.html',
  styleUrls: ['./column-filter.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, PopoverModule, ListboxModule, ButtonModule, RippleModule],
})
export class ColumnFilterComponent implements OnInit {
  @Input() tableName!: keyof ITableColumnFilter;
  @Input() columnName!: string;
  @Input() displayName?: string;
  @Input() disabled: boolean = false;

  @Output() filterPanelOpened: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('columnFilterListbox') columnFilterListbox?: Listbox;
  @ViewChild('filterPopover') filterPopover?: Popover;

  filterActive: boolean = false;

  filterOptions: any[] = [];
  oldSelectedFilterOptions: { label: string; value: string; title: string; disabled: boolean }[] =
    [];
  selectedFilterOptions: { label: string; value: string; title: string; disabled: boolean }[] = [];

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private _columnFilterService: ColumnFilterService,
    private appState: AppStateService,
    private loggingService: LoggingService,
  ) { }

  ngOnInit(): void {
    this._columnFilterService.filterPanelOpened$
      .pipe(takeUntil(this._destroying$))
      .subscribe(colName => {
        if (this.columnName !== colName) {
          this.selectedFilterOptions = this.oldSelectedFilterOptions;
        }
      });
    this.updateFilterValues();
  }

  updateFilterValues() {
    const activeFilters = this._columnFilterService.getActiveTableFilterValues(
      this.tableName,
      this.columnName,
    );
    const availableRowCount = this._columnFilterService.getOtherFiltersRowCounts(
      this.tableName,
      this.columnName,
    );
    const availableFilters = this._columnFilterService.getDisplayTableFilterValues(
      this.tableName,
      this.columnName,
    );

    this.selectedFilterOptions = Array.from(activeFilters.values() ?? []).map(x => ({
      label: x,
      value: x,
      title: availableRowCount[x]?.toString() ?? '0',
      disabled: !availableFilters.has(x),
    }));

    if (this.selectedFilterOptions.length) {
      this.filterActive = true;
    }

    this.oldSelectedFilterOptions = this.selectedFilterOptions;

    const alreadySelectedFilters = new Set<string>();
    const selectedItems: any[] = [];
    const availableItems: any[] = [];

    this.selectedFilterOptions.forEach(x => {
      alreadySelectedFilters.add(x.label);
      selectedItems.push(x);
    });

    Array.from(availableFilters.values() ?? [])
      .sort()
      .filter(x => !alreadySelectedFilters.has(x))
      .forEach(x => {
        availableItems.push({
          label: x,
          value: x,
          title: availableRowCount[x]?.toString() ?? '0',
        });
      });

    this.filterOptions = [...selectedItems, ...availableItems];
  }

  clearFilter() {
    this.filterActive = false;
    this.selectedFilterOptions = [];
    this.oldSelectedFilterOptions = [];
    this._columnFilterService.clearTableColumnFilter(this.tableName, this.columnName);
    this.updateFilterValues();
  }

  clearAllFilters() {
    this._columnFilterService.resetTableFilter(this.tableName);
    this.updateFilterValues();
    this.closeFilterPanel();
  }

  applyFilter() {
    if (!this.selectedFilterOptions || this.selectedFilterOptions.length === 0) {
      this.clearFilter();
    } else {
      this.filterActive = true;
      this.oldSelectedFilterOptions = this.selectedFilterOptions;
      this._columnFilterService.setTableFilterValues(
        this.tableName,
        this.columnName,
        this.selectedFilterOptions.map(x => x.label),
      );

      const activeFilters = this._columnFilterService.getActiveTableFilters(this.tableName);
      const filteredColumnNames = Object.keys(activeFilters);

      let totalFilteredItems = 0;
      const filteredColumns = filteredColumnNames
        .map(columnName => {
          totalFilteredItems += activeFilters[columnName].size;
          return `${columnName} (${activeFilters[columnName].size})`;
        })
        .join(', ');

      const multiselectProps: { [key: string]: number | string } = {
        ExperimentId: this.appState.GetCurrentExperiment()?.experimentId ?? 0,
        DeviceType: this.appState.GetCurrentExperiment()?.deviceType ?? -1,
        ExperimentOwner: this.appState.GetCurrentExperiment()?.createdBy ?? 0,
        MultiselectAction: EVENT_APPLY_COLUMN_FILTER,
        FilterView: this.tableName,
        FilteredColumns: filteredColumns,
        NumFilteredColumns: filteredColumnNames.length,
        TotalFilteredItems: totalFilteredItems,
      };

      this.loggingService.logEvent(EVENT_APPLY_COLUMN_FILTER, multiselectProps);
    }
    this.updateFilterValues();
    this.closeFilterPanel();
  }

  closeFilterPanel() {
    this.selectedFilterOptions = this.oldSelectedFilterOptions;
    this.filterPopover?.hide();
  }

  onFilterPanelHide() {
    this.selectedFilterOptions = this.oldSelectedFilterOptions;
  }
}
